import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography,
  Modal,
  Skeleton,
  Layout,
  Tabs,
  Badge,
  Descriptions,
  Space,
} from 'antd';
import CustomButton from '../../../components/CustomButton';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import ToCheck from '../Validation/ToCheck/index';
import Rejected from '../Validation/Rejected/index';
import { ajaxTable } from '../../../api/module/announcement_preselection';
import { filter as getEmailsById } from '../../../api/module/announcement_preselection_email';

import './style.scss';
import ValidationEmailHistory from '../../../components/ValidationEmailHistory';
import { useParams } from 'react-router-dom';
import { AdvancedPersonSearch, AjaxTable } from '../../../components';
import { announcementPreselection } from '../../../api/apiActions';
import { getAPICall } from '../../../api';
import { ClockCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;
const { Content } = Layout;
const { TabPane } = Tabs;

const initCounters = () => ({
  apply: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  preselected: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  pending: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  reject: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
});

const AnnouncementValidation = () => {
  const { announcement_id, announcement_rol_id } = useParams();
  const [info, setInfo] = useState(null);
  const [loadingForm, setLoadingForm] = useState(true);
  const [counters, setCounters] = useState(initCounters);
  const [activeKey, setActiveKey] = useState('toCheck');
  const [modalData, setModalData] = useState(null);
  const [modalHistoryData, setModalHistoryData] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [advancedSearch, setAdvancedSearch] = useState(undefined);

  const breadcrumbItems = [
    {
      text: 'Preseleccionados',
      to: `${INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION}`,
    },
    {
      text: 'Validación',
    },
  ];

  const getInfo = async () => {
    try {
      setLoadingForm(true);
      const { data: aData } = await getAPICall(
        announcementPreselection.info(announcement_rol_id),
      );
      setInfo(aData);
      setLoadingForm(false);
    } catch (err) {
      console.log(err);
    }
  };

  const getCounters = async () => {
    setCounters(initCounters);
    const { data } = await getAPICall(
      announcementPreselection.counters(announcement_rol_id),
      { advanced_search: advancedSearch },
    );
    setCounters(data.results);
  };

  useEffect(() => {
    getInfo();
  }, []);

  const showHistory = async (record) => {
    setHistoryLoading(true);
    let response = await getEmailsById(
      `?filter[convocatoria_tercero]=${record.id}`,
    );
    setModalData({ ...info, ...record });
    setModalHistoryData(response.data.results);
    setTimeout(() => {
      setHistoryOpen(true);
      setHistoryLoading(false);
    }, 500);
  };

  const columnsPreselected = [
    {
      title: 'Correo electrónico',
      dataIndex: 'notificacion_email',
    },
    {
      title: 'Nombres y Apellidos',
      dataIndex: 'nombres_apellidos',
    },
    {
      title: 'Identificación',
      dataIndex: 'identificacion',
    },
    {
      title: 'Correo notificación',
      dataIndex: 'estado_preseleccion',
      key: 'estado_preseleccion',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <CustomButton
            block
            type="default"
            htmlType={'button'}
            text={
              <Link
                to={`${INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION_EMAIL}/${announcement_id}/${record.id}/${announcement_rol_id}/${record.tercero}?email=preselected`}
              >
                Generar correo{' '}
              </Link>
            }
          />
          {record.estado_envio_preseleccionado && (
            <Paragraph style={{ marginBottom: 0 }}>Enviado</Paragraph>
          )}
        </Space>
      ),
    },
  ];

  const columnsAdjustInformation = [
    {
      title: 'Correo electrónico',
      dataIndex: 'notificacion_email',
      align: 'center',
    },
    {
      title: 'Nombres y Apellidos',
      dataIndex: 'nombres_apellidos',
      align: 'center',
    },
    {
      title: 'Identificación',
      dataIndex: 'identificacion',
      align: 'center',
    },
    {
      title: 'Enviar Correo notificación',
      dataIndex: 'estado_preseleccion',
      key: 'estado_preseleccion',
      render: (_, record) => (
        <Space>
          <CustomButton
            block
            type="default"
            htmlType={'button'}
            className={'ant-btn-info'}
            text={
              <Link
                to={`${INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION_EMAIL}/${announcement_id}/${record.id}/${announcement_rol_id}/${record.tercero}?email=adjust-information`}
              >
                Generar correo{' '}
              </Link>
            }
          />
          {record.estado_envio_ajustar && (
            <Paragraph style={{ marginBottom: 0 }}>Enviado</Paragraph>
          )}
        </Space>
      ),
    },
    {
      title: 'Histórico',
      dataIndex: 'id',
      key: 'id',
      render: (_, record) => (
        <CustomButton
          block
          loading={historyLoading}
          type="default"
          onClick={() => {
            showHistory(record);
          }}
          text={'Ver'}
        />
      ),
    },
  ];

  return (
    <Content>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Módulo de Preselección - Validación
      </Title>
      <Skeleton loading={loadingForm} active>
        {info != null && (
          <Descriptions
            layout="vertical"
            title="Información de la convocatoria"
            column={3}
            bordered
          >
            <Descriptions.Item label="Número de convocatoria">
              {info.convocatoria_numero}
            </Descriptions.Item>
            <Descriptions.Item label="Nombre de la convocatoria">
              {info.convocatoria_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Organización">
              {info.organizacion_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Proyecto">
              {info.nombre_proyecto}
            </Descriptions.Item>
            <Descriptions.Item label="Tipo de convocatoria">
              {info.convocatoria_tipo ? 'Abierta' : 'Cerrada'}
            </Descriptions.Item>
            <Descriptions.Item label="Cargo/Rol">
              {info.cargo}
            </Descriptions.Item>
            <Descriptions.Item label="Lugar de realización labores">
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {(info.lugar || '').split(',;,').map((role, idx) => (
                  <span key={idx}>{role.trim()}</span>
                ))}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Aliado para el cual se requiere esta convocatoria">
              {info.aliado_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Total vacantes">
              {info.total_vacantes}
            </Descriptions.Item>
          </Descriptions>
        )}
        <br />
        <AdvancedPersonSearch
          title="Búsqueda de perfiles de las personas que aplicaron a la convocatoria"
          infoEndpoint={announcementPreselection.searchInfo}
          onFinish={setAdvancedSearch}
        />
        <Tabs
          type="card"
          defaultActiveKey="toCheck"
          activeKey={activeKey}
          onChange={setActiveKey}
          style={{ marginTop: 15 }}
          destroyInactiveTabPane={true}
        >
          <TabPane
            tab={
              <Paragraph>
                Por revisar
                <Badge showZero count={counters ? counters.apply : 0} />
              </Paragraph>
            }
            key="toCheck"
          >
            <ToCheck
              announcement_id={announcement_id}
              announcement_rol_id={announcement_rol_id}
              advancedSearch={advancedSearch}
              onRefresh={getCounters}
            />
          </TabPane>
          <TabPane
            tab={
              <Paragraph>
                Preseleccionados
                <Badge showZero count={counters ? counters.preselected : 0} />
              </Paragraph>
            }
            key="preselected"
          >
            <AjaxTable
              columns={columnsPreselected}
              pageSize={10}
              tableTitle={'Listado de contratistas preseleccionados'}
              endpoint={ajaxTable}
              searchInput
              exportCSV
              reloadButton
              query={{
                convocatoria_rol: announcement_rol_id,
                estado_tercero: 11,
                advanced_search: advancedSearch,
              }}
              setCount={getCounters}
              showSizeChanger
            />
          </TabPane>
          <TabPane
            tab={
              <Paragraph>
                Ajustar información (Pendiente)
                <Badge showZero count={counters ? counters.pending : 0} />
              </Paragraph>
            }
            key="adjust_information"
          >
            <AjaxTable
              columns={columnsAdjustInformation}
              pageSize={10}
              tableTitle={'Listado de contratistas para ajustar información'}
              endpoint={ajaxTable}
              searchInput
              exportCSV
              reloadButton
              query={{
                convocatoria_rol: announcement_rol_id,
                estado_tercero: 12,
                advanced_search: advancedSearch,
              }}
              setCount={getCounters}
              showSizeChanger
            />
          </TabPane>
          <TabPane
            tab={
              <Paragraph>
                Rechazados
                <Badge showZero count={counters ? counters.reject : 0} />
              </Paragraph>
            }
            key="rejected"
          >
            <Rejected
              announcement_id={announcement_id}
              announcement_rol_id={announcement_rol_id}
              advancedSearch={advancedSearch}
              onRefresh={getCounters}
            />
          </TabPane>
        </Tabs>
        <CustomButton
          type={'default'}
          className={'back-button'}
          htmlType="button"
          text={
            <Link to={`${INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION}`}>
              Volver
            </Link>
          }
        />
      </Skeleton>
      <Modal
        title={
          <Title level={4} style={{ marginBottom: 0 }}>
            Histórico de correos electrónicos enviados - Preselección
          </Title>
        }
        open={historyOpen}
        onCancel={() => {
          setHistoryOpen(false);
        }}
        cancelText="Cerrar"
        width={1000}
        okButtonProps={{ style: { display: 'none' } }}
      >
        <ValidationEmailHistory
          validationData={modalData}
          dataSource={modalHistoryData}
        />
      </Modal>
    </Content>
  );
};

export default AnnouncementValidation;
